import { type } from 'arktype'

import { financialRateSchema, newFinancialRate, type FinancialRate } from '@orus.eu/amount'
import { calendarDateType } from '@orus.eu/calendar-date'
import { type Product, productSchema } from '@orus.eu/product'

export const PENDING_ORG_SUBSCRIPTION_ARCHIVAL_DELAY_DAYS = 15

export const organizationStatusSchema = type('===', 'pending', 'active', 'disabled', 'archived')
export type OrganizationStatus = typeof organizationStatusSchema.infer

/** The statuses an organization can be created in: pending (early access) or directly active (oneshot) */
export type OrganizationInitialStatus = Extract<OrganizationStatus, 'pending' | 'active'>

export function isOrganizationStatus(value: string): value is OrganizationStatus {
  return organizationStatusSchema.allows(value)
}

const allowedStatusTransitions: Record<OrganizationStatus, readonly OrganizationStatus[]> = {
  pending: ['active', 'archived'],
  active: ['disabled'],
  disabled: ['active'],
  archived: [],
}

/** The statuses an organization currently in status is allowed to transition to */
export function getAllowedStatusTransitions(status: OrganizationStatus): readonly OrganizationStatus[] {
  return allowedStatusTransitions[status]
}

/** Statuses an org can be transitioned to (pending is only the creation state) */
export const organizationStatusTransitionTargetSchema = type('===', 'active', 'disabled', 'archived')
export type OrganizationStatusTransitionTarget = typeof organizationStatusTransitionTargetSchema.infer

export const organizationTypeSchema = type('===', 'orus', 'broker', 'partner')
export type OrganizationType = typeof organizationTypeSchema.infer
export const organizationTypeValues = ['broker', 'partner'] as const satisfies readonly OrganizationType[]

export function isOrganizationType(value: string): value is OrganizationType {
  return organizationTypeSchema.allows(value)
}

const insideAnimatorEmailSchema = type(
  '===',
  'francois.coste@orus.eu',
  'aurelien.proust@orus.eu',
  'benoit.guichard@orus.eu',
  'emma.luthringer@orus.eu',
  'alexis.menhem@orus.eu',
  'elie.ghaoui@orus.eu', // a lot of orgs are having Elie set as inside animator
  'anna.mourelon@orus.eu',
  'landry-raphael.quin@orus.eu',
  'bilel.mansouri@orus.eu',
  'jason.lebsir@orus.eu',
)

const fieldInspectorEmailSchema = type(
  '===',
  'tarik.kaci@orus.eu',
  'franck.rinaldo@orus.eu',
  'karamba.diaby@orus.eu',
  'johan.phanzu@orus.eu',
)

const onboardingSpecialistEmailSchema = type('===', 'elie.ghaoui@orus.eu')

const sdrEmailSchema = type('===', 'antoine.devillard@orus.eu', 'yassim.balta@orus.eu')

export function isInsideAnimatorEmail(value: string): value is typeof insideAnimatorEmailSchema.infer {
  return insideAnimatorEmailSchema.allows(value)
}

export function isFieldInspectorEmail(value: string): value is typeof fieldInspectorEmailSchema.infer {
  return fieldInspectorEmailSchema.allows(value)
}

export function isOnboardingSpecialistEmail(value: string): value is typeof onboardingSpecialistEmailSchema.infer {
  return onboardingSpecialistEmailSchema.allows(value)
}

export function isSdrEmail(value: string): value is typeof sdrEmailSchema.infer {
  return sdrEmailSchema.allows(value)
}

export type SdrContact = { email: string; fullName: string; phone: string }

/**
 * Contact details for each SDR
 * The email values must stay in sync with sdrEmailSchema above
 */
export const sdrContacts = [
  { email: 'antoine.devillard@orus.eu', fullName: 'Antoine Devillard', phone: '+33744096525' },
  { email: 'yassim.balta@orus.eu', fullName: 'Yassim Balta', phone: '+33744093604' },
] as const satisfies readonly SdrContact[]

export const automatedCciCertificateDistributionSchema = type({
  active: 'boolean',
  distributeBeforeDays: '1 <= number.integer <= 70',
  sendToEmail: 'string',
  companyName: 'string',
  cpiNumber: 'string',
  cciName: 'string',
  legalRepresentatives: type({
    title: "'Monsieur' | 'Madame' | null",
    qualities: type('("Gérant" | "Président" | "Directeur général")[]'),
    name: 'string',
  }).array(),
  'headQuarterAddress?': 'string | null | undefined',
  'complementaryInformation?': 'string | null | undefined',
  'dateOfIssue?': calendarDateType.or('null | undefined'),
  'dateOfValidity?': calendarDateType.or('null | undefined'),
}).narrow((value, ctx) => {
  if (value.dateOfIssue == null && value.dateOfValidity == null) {
    return ctx.mustBe('have at least one of dateOfIssue or dateOfValidity set')
  }
  return true
})
export type AutomatedCciCertificateDistribution = typeof automatedCciCertificateDistributionSchema.infer

const commissionRateByProductSchema = type
  .Record('string', financialRateSchema.or(type.undefined))
  .narrow<Record<Product, FinancialRate | undefined>>(() => true)
  .partial()

export type CommissionRateByProduct = typeof commissionRateByProductSchema.infer

export const organizationFieldsV2Schema = type({
  technicalName: 'string',
  displayName: 'string',
  email: 'string',
  type: organizationTypeSchema,
  isManagedByOrus: 'boolean',
  onboardingSpecialistEmail: onboardingSpecialistEmailSchema,
  'insideAnimatorEmail?': insideAnimatorEmailSchema.or(type.undefined),
  'fieldInspectorEmail?': fieldInspectorEmailSchema.or(type.undefined),
  'sdrEmail?': sdrEmailSchema.or(type.undefined),
  selectableProducts: productSchema.array(),
  commissionRate: commissionRateByProductSchema,
  'orias?': 'string | undefined',
  'legalEntity?': 'string | undefined',
  'address?': 'string | undefined',
  'iban?': 'string | undefined',
  'phone?': 'string | undefined',
  'emailDomain?': 'string | undefined',
  'hasEmbeddedFunnel?': 'boolean | undefined',
  'embeddingPartnerId?': 'string | undefined',
  'canTerminateContracts?': 'boolean | undefined',
  'canEditPartnerFees?': 'boolean | undefined',
  'canSendOrderCompletedEvent?': 'boolean | undefined',
  'defaultPartnerManagementFeeRate?': '(0 <= number <= 0.2) | undefined',
  'defaultPartnerApplicationFee?': '(number >= 0) | undefined',
  // No explicit undefined: an update clears a field received as undefined, and no caller clears the API key fields that way
  'apiTokenHashes?': 'string[]',
  'apiTokenLabels?': type.Record('string', 'string'),
  'contractLifecycleEventWebhookUrl?': 'string | undefined',
  'billingEmails?': 'string[] | undefined',
  'managementEmail?': 'string | undefined',
  'automatedCciCertificateDistribution?': automatedCciCertificateDistributionSchema.or(type.undefined),
})

export type OrganizationV2Fields = typeof organizationFieldsV2Schema.infer

export type OrganizationV2UpdatableFields = Omit<OrganizationV2Fields, 'technicalName'>

export type OrganizationV2 = OrganizationV2Fields & {
  status: OrganizationStatus
  createdAt: number
  archivedAt: number | null
}

export type OrganizationPricingInfo = Pick<OrganizationV2, 'type' | 'commissionRate'>

/**
 * Fields required for an organization to transition into (or be created in) the
 * active status, pending orgs may omit these.
 */
export const organizationV2ActiveRequiredFields = [
  'orias',
  'legalEntity',
  'address',
  'insideAnimatorEmail',
] as const satisfies ReadonlyArray<keyof OrganizationV2Fields>

/** String fields that must always carry a non-empty value, whatever the status */
export const organizationV2AlwaysRequiredFields = ['displayName', 'email'] as const satisfies ReadonlyArray<
  keyof OrganizationV2Fields
>

export const isOrganizationActive = (organization: Pick<OrganizationV2, 'status'>): boolean =>
  organization.status === 'active'

export function isActiveNotManagedByOrus(organization: Pick<OrganizationV2, 'isManagedByOrus' | 'status'>): boolean {
  return !organization.isManagedByOrus && isOrganizationActive(organization)
}

export const isOrganizationActiveOrPending = (organization: Pick<OrganizationV2, 'status'>): boolean =>
  organization.status === 'active' || organization.status === 'pending'

/** Blank counts as absent, so that clearing the field in the backoffice removes the address */
export function normalizeManagementEmail(managementEmail: string | null | undefined): string | undefined {
  const trimmed = managementEmail?.trim()
  return trimmed ? trimmed : undefined
}

export function resolveOrganizationContactEmail(
  organization: Pick<OrganizationV2Fields, 'email' | 'managementEmail'>,
): string {
  return organization.managementEmail ?? organization.email
}

export const organizationV2Mock: OrganizationV2 = {
  technicalName: 'orus',
  displayName: 'Orus',
  email: 'contact@orus.eu',
  type: 'orus',
  isManagedByOrus: true,
  insideAnimatorEmail: 'emma.luthringer@orus.eu',
  onboardingSpecialistEmail: 'elie.ghaoui@orus.eu',
  selectableProducts: ['mrph', 'mrpw', 'rcph', 'muta', 'rcda', 'es-rcph', 'rcpa', 'mrpa', 'fr-prel', 'nl-rcph'],
  commissionRate: { rcph: newFinancialRate('0.1') },
  orias: '22002459',
  legalEntity: 'Orus SAS',
  address: '40 rue de Paradis, 75010 Paris',
  status: 'active',
  createdAt: 0,
  archivedAt: null,
}

export const orusOrganization = 'orus'

export const orusTestOrganization = 'orus-test'

export type OrganizationFunnelInfo = Pick<
  OrganizationV2,
  'technicalName' | 'email' | 'phone' | 'hasEmbeddedFunnel' | 'isManagedByOrus'
>

/** Sibling org fields, not a nested object: patches can touch either key, and the flag can be off while an id remains. */
export type EmbeddedFunnelFields = Pick<OrganizationV2Fields, 'hasEmbeddedFunnel' | 'embeddingPartnerId'>
